import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ExternalLink, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const links = [
  { to: "/admin/crm", label: "CRM" },
  { to: "/admin/portafolio", label: "Portafolio" },
  { to: "/admin/team", label: "Equipo" },
  { to: "/admin/appointments", label: "Citas" },
  { to: "/admin/tickets", label: "Tickets" },
];

export default function AdminHeader() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)]/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3.5 sm:px-8 lg:px-10">
        {/* Left: brand + nav */}
        <div className="flex items-center gap-6">
          <NavLink
            to="/admin"
            className="flex items-center gap-2 text-sm font-bold text-[var(--color-text-primary)] transition-opacity hover:opacity-70"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            <LayoutDashboard className="h-4 w-4 text-[var(--color-accent)]" />
            ta<span className="text-[var(--color-accent)]">Code</span>
            <span className="ml-1 rounded bg-[var(--color-accent)]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-accent)]">
              Admin
            </span>
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-0.5 md:flex">
            {links.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `relative rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    isActive
                      ? "text-[var(--color-text-primary)] bg-[var(--color-surface-hover)]"
                      : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {label}
                    {isActive && (
                      <span className="absolute bottom-0.5 left-2 right-2 h-px rounded-full bg-[var(--color-accent)]" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Right: actions */}
        <div className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Ver sitio
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] transition-colors hover:text-red-400 hover:bg-red-500/8"
          >
            <LogOut className="h-3.5 w-3.5" />
            Salir
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="rounded-lg p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] md:hidden"
        >
          {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="border-t border-[var(--color-border)] bg-[var(--color-bg-primary)] px-5 py-3 md:hidden">
          <ul className="space-y-0.5">
            {links.map(({ to, label }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "text-[var(--color-accent)] bg-[var(--color-accent)]/8"
                        : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]"
                    }`
                  }
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex gap-2 border-t border-[var(--color-border)] pt-3">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
            >
              <ExternalLink className="h-4 w-4" />
              Ver sitio
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:text-red-400"
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
